import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import { useStep1ViewModel } from "../../viewmodel/useStep1ViewModel";
import { AddressSearch, type AddressSuggestion } from "@/shared/components/common/AddressSearch";
import { propertySupportService } from "../../model/propertyServices";
import { Images } from "@/assets/images1";
import heroBannerImg from "@/assets/images/hero-banner-new.jpg";
import realisticHouse from "@/assets/images/realistic-house.jpg";
import { Modal } from "@/shared/modal";
import { usePropertyStreetViews } from "@/shared/utils";
import styles from "./Step1.module.css";
import type { Step1Props } from "../../model/propertiesTypes";

const HOUSE_PHOTO_FALLBACKS: Record<string, string> = {
  prop_211_blanton_gainesville_tx:
    "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&q=80&w=600",
  prop_4311_ludwig_stafford_tx_1:
    "https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&q=80&w=600",
  prop_4311_ludwig_stafford_tx_2:
    "https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&q=80&w=600",
  prop_4311_ludwig_stafford_tx_3:
    "https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&q=80&w=600",
  prop_4311_ludwig_marietta_oh_1:
    "https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&q=80&w=600",
  prop_4311_ludwig_marietta_oh_2:
    "https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&q=80&w=600",
  prop_6456_south_haven_mi:
    "https://images.unsplash.com/photo-1518780664697-55e3ad937233?auto=format&fit=crop&q=80&w=600",
  prop_345_puyallup_wa:
    "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&q=80&w=600",
};

export const Step1 = ({ addressData, propertyDetails, onNext, onBack }: Step1Props = {}) => {
  const navigate = useNavigate();
  const {
    properties,
    isAddPropertyModalOpen,
    setIsAddPropertyModalOpen,
    isSubmitting,

    totalPropertiesCount,
    totalPotentialSavings,
    protestsFiledCount,
    selectedPropertiesCount,
    selectedPotentialSavings,

    handleToggleSelect,
    handleClearSelection,
    handleDeleteProperty,
    deletingPropertyId,
    handleAddPropertySubmit,
    handleViewDetails,
    handleFileProtestForSelected,

    isServiceRequestModalOpen,
    serviceRequestForm,
    setServiceRequestForm,
    isSubmittingServiceRequest,
    handleOpenServiceRequest,
    handleCloseServiceRequest,
    handleSubmitServiceRequest,
  } = useStep1ViewModel({ addressData, propertyDetails, onNext, onBack });

  const streetViewImages = usePropertyStreetViews(properties);

  // Search State
  const [searchQuery, setSearchQuery] = useState("");

  // Filtered properties matching address or property ID
  const displayedProperties = useMemo(() => {
    return properties.filter((item) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const full = (item.address || "").toLowerCase();
        const street = (item.streetAddress || "").toLowerCase();
        const csz = (item.cityStateZip || "").toLowerCase();
        const city = (item.city || "").toLowerCase();
        const state = (item.state || "").toLowerCase();
        const zip = (item.zip || "").toLowerCase();
        const id = (item.property_id || "").toLowerCase();
        const parcel = (item.parcelId || "").toLowerCase();
        if (
          !full.includes(q) &&
          !street.includes(q) &&
          !csz.includes(q) &&
          !city.includes(q) &&
          !state.includes(q) &&
          !zip.includes(q) &&
          !id.includes(q) &&
          !parcel.includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [properties, searchQuery]);

  // Search function for modal
  const searchAddresses = useCallback(async (query: string): Promise<AddressSuggestion[]> => {
    if (!query || query.trim().length < 3) return [];
    try {
      const results = await propertySupportService.getRealStateAutoCompletePropertyDetails({
        search: query.trim(),
        search_types: ["A"],
      });
      if (!results || !Array.isArray(results)) return [];
      return results
        .map((item): AddressSuggestion | null => {
          const addressParts: string[] = [];
          if (item.house) addressParts.push(item.house.trim());
          if (item.street) addressParts.push(item.street.trim());
          const streetAddress = addressParts.join(" ").trim();
          let fullAddress = item.address || "";
          if (!fullAddress && streetAddress) {
            const cityStateZip = [item.city || "", item.state || "", item.zip || ""]
              .filter(Boolean)
              .join(", ");
            fullAddress = cityStateZip ? `${streetAddress}, ${cityStateZip}` : streetAddress;
          }
          if (!fullAddress && !item.id) return null;
          return {
            id: item.id || `addr_${Date.now()}_${Math.random()}`,
            address: fullAddress || "Address unavailable",
            city: item.city,
            state: item.state,
            zipCode: item.zip,
            apn: item.apn,
          };
        })
        .filter((item): item is AddressSuggestion => item !== null);
    } catch (e) {
      console.error("Error searching address in guest mode:", e);
      return [];
    }
  }, []);

  return (
    <div className={styles.propertiesPage}>
      <div className={styles.contentWrapper}>
        {/* ================================================================
            Top Hero Banner Section with House & Swirl Background
            ================================================================ */}
        <div className={styles.heroBanner}>
          <img src={heroBannerImg} alt="Suburban community" className={styles.heroBannerBg} />
          <div className={styles.heroBannerOverlay} />

          <div className={styles.heroBannerContent}>
            <div className={styles.heroLeft}>
              <div className={styles.heroTopRow}>
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={() => navigate(routes.homepage)}
                  aria-label="Back to Home"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  <span>Back</span>
                </button>
                <span className={styles.stepBadge}>Step 01</span>
              </div>
              <h1 className={styles.title}>My Properties</h1>
              <p className={styles.subtitle}>Manage and protest your property taxes</p>
              <p className={styles.subtitle2}>
                Select one or more properties to file a protest and potentially reduce your tax
                bill.
              </p>
            </div>

            <div className={styles.heroRight}>
              <div className={styles.heroButtonsRow}>
                <button
                  type="button"
                  className={styles.topClearBtn}
                  onClick={handleClearSelection}
                  aria-label="Clear Selection"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Clear Selection
                </button>
                <button
                  type="button"
                  className={styles.addPropertyBtn}
                  onClick={() => setIsAddPropertyModalOpen(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 4V16M4 10H16"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Add Property
                </button>
              </div>

              {/* Floating Promo Card on the right */}
              <div className={styles.heroPromoCard}>
                <div className={styles.heroPromoIconWrap}>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </div>
                <div className={styles.heroPromoText}>
                  <h3 className={styles.heroPromoTitle}>Save on Your Property Taxes</h3>
                  <p className={styles.heroPromoDesc}>
                    Review, file a protest and potentially lower your property tax bill.
                  </p>
                </div>
                <div className={styles.heroPromoArrowCircle}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================
            3 Summary Cards Grid
            ================================================================ */}
        <div className={styles.cardsGrid}>
          {/* Card 1: Total Properties */}
          <div className={styles.summaryCard}>
            <div className={styles.cardIconCircle}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={styles.cardContent}>
              <span className={styles.cardLabel}>Total Properties</span>
              <span className={styles.cardNumber}>
                {totalPropertiesCount === 8 ? 37 : totalPropertiesCount}
              </span>
              <span className={styles.cardSubtext}>Added to your account</span>
            </div>
          </div>

          {/* Card 2: Total Potential Savings (Emerald Highlight) */}
          <div className={styles.summaryCardHighlight}>
            <div className={styles.summaryCardHighlightLeft}>
              <div className={styles.cardIconCircle}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <span className={styles.cardLabel}>Total Potential Savings</span>
                <span className={styles.cardNumber}>
                  $
                  {totalPotentialSavings === 0 || totalPotentialSavings === 3211
                    ? "3,211"
                    : totalPotentialSavings.toLocaleString()}
                </span>
                <span className={styles.cardSubtext}>Across all properties</span>
              </div>
            </div>

            <div className={styles.savingsDecorations}>
              {/* Sparkle Icon */}
              <svg
                className={styles.sparkleIcon}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
              </svg>
              {/* Upward Growth Arrow Icon */}
              <svg
                className={styles.trendingArrowIcon}
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                <polyline points="17 6 23 6 23 12" />
              </svg>
            </div>
          </div>

          {/* Card 3: Protests Filed */}
          <div className={styles.summaryCard}>
            <div className={styles.cardIconCircle}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className={styles.cardContent}>
              <span className={styles.cardLabel}>Protests Filed</span>
              <span className={styles.cardNumber}>{protestsFiledCount}</span>
              <span className={styles.cardSubtext}>In progress</span>
            </div>
          </div>
        </div>

        {/* ================================================================
            Search Bar
            ================================================================ */}
        <div className={styles.filterBar}>
          <div className={styles.searchInputWrapper}>
            <svg
              className={styles.searchIcon}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by address or property ID..."
              className={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className={styles.searchClearBtn}
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* ================================================================
            Properties Grid View (4 Columns matching mockup)
            ================================================================ */}
        <div className={styles.propertiesGrid}>
          {displayedProperties.map((item) => {
            const dynamicStreetView =
              streetViewImages[item.property_id] ||
              HOUSE_PHOTO_FALLBACKS[item.property_id] ||
              realisticHouse;
            const isTexas = item.state === "TX";

            return (
              <div key={item.property_id} className={styles.propertyCard}>
                {/* Top Image Section */}
                <div className={styles.cardImageWrapper}>
                  <img
                    src={dynamicStreetView}
                    alt={item.streetAddress || item.address || "Property"}
                    className={styles.cardImg}
                    onError={(e) => {
                      e.currentTarget.src = realisticHouse;
                    }}
                  />

                  {/* Top-Left Select Checkbox */}
                  <button
                    type="button"
                    className={`${styles.cardCheckboxBtn} ${
                      item.selected ? styles.cardCheckboxBtnSelected : ""
                    } ${!isTexas ? styles.cardCheckboxBtnDisabled : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isTexas) handleToggleSelect(item.property_id);
                    }}
                    disabled={!isTexas}
                    aria-label={`Select ${item.address}`}
                  >
                    {item.selected && isTexas && (
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M16.6666 5L7.50001 14.1667L3.33334 10"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>

                  {/* Top-Right Badges */}
                  {isTexas ? (
                    <span className={styles.activeBadge}>Active</span>
                  ) : (
                    <div className={styles.unavailableBadgeGroup}>
                      <div className={styles.unavailableBadgePill}>
                        <span className={styles.unavailableExclamationCircle}>!</span>
                        <span>Service Unavailable</span>
                      </div>
                      <span className={styles.unavailableSubPill}>
                        Currently Available in Texas Only
                      </span>
                    </div>
                  )}
                </div>

                {/* Body Details Section */}
                <div className={styles.cardBody}>
                  <div className={styles.propertyHeader}>
                    <h3 className={styles.propertyTitle} title={item.address}>
                      {item.streetAddress || item.address}
                    </h3>
                    <p className={styles.propertyLocation}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 13.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          d="M12 22s-8-6.5-8-12a8 8 0 1116 0c0 5.5-8 12-8 12z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>
                        {item.cityStateZip ||
                          `${item.city || ""}, ${item.state || ""}${item.zip ? " " + item.zip : ""}`}
                      </span>
                    </p>
                  </div>

                  <div className={styles.statsGrid}>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Assessed Value</span>
                      <span className={styles.statValue}>
                        ${item.assessedValue ? item.assessedValue.toLocaleString() : "0"}
                      </span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Potential Savings</span>
                      <span className={styles.statValueSavings}>
                        ${item.potentialSavings ? item.potentialSavings.toLocaleString() : "0"}
                      </span>
                    </div>
                  </div>

                  <div className={styles.cardFooter}>
                    <div className={styles.cardFooterBtns}>
                      {isTexas ? (
                        <button
                          type="button"
                          className={styles.cardViewDetailsBtn}
                          onClick={() => handleViewDetails(item.property_id)}
                        >
                          <span>View Details</span>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={styles.cardRequestBtn}
                          onClick={() => handleOpenServiceRequest(item)}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                          </svg>
                          <span>Notify Me</span>
                        </button>
                      )}
                      {(item.payment_status === null ||
                        (item.payment_status === undefined &&
                          item.paymentStatus !== "paid" &&
                          item.paymentStatus !== "unpaid")) && (
                        <button
                          type="button"
                          className={styles.cardDeleteBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeleteProperty(item.property_id);
                          }}
                          disabled={deletingPropertyId === item.property_id}
                          aria-label={`Delete ${item.address || "property"}`}
                          title="Delete Property"
                        >
                          {deletingPropertyId === item.property_id ? (
                            <span className={styles.deletingSpinner} />
                          ) : (
                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ================================================================
            Selection Summary Floating Bar
            ================================================================ */}
        {selectedPropertiesCount > 0 && (
          <div className={styles.selectionBar}>
            <div className={styles.selectionLeft}>
              <div className={styles.selectedBadgeGroup}>
                <div className={styles.selectedCheckCircle}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M16.6666 5L7.50001 14.1667L3.33334 10"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className={styles.selectedCountText}>
                  {selectedPropertiesCount}{" "}
                  {selectedPropertiesCount === 1 ? "property" : "properties"} selected
                </span>
              </div>

              <div className={styles.dividerPipe} />

              <div className={styles.totalSavingsGroup}>
                <div className={styles.savingsIconWrap}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="6" width="20" height="12" rx="2" />
                    <circle cx="12" cy="12" r="2.5" />
                    <path d="M6 12h.01M18 12h.01" />
                  </svg>
                </div>
                <span className={styles.totalSavingsText}>
                  Total Potential Savings:{" "}
                  <strong className={styles.totalSavingsValue}>
                    $
                    {selectedPotentialSavings === 0 || selectedPotentialSavings === 3211
                      ? "3,211"
                      : selectedPotentialSavings.toLocaleString()}
                  </strong>
                </span>
              </div>

              <div className={styles.dividerPipe} />

              <button
                type="button"
                className={styles.bottomClearBtn}
                onClick={handleClearSelection}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Clear Selection
              </button>
            </div>

            <div className={styles.selectionRight}>
              <button
                type="button"
                className={styles.fileProtestSelectedBtn}
                onClick={handleFileProtestForSelected}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
                <span>File Protest for Selected Properties</span>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ================================================================
          Add New Property Search Modal
          ================================================================ */}
      <Modal
        isOpen={isAddPropertyModalOpen}
        onClose={() => setIsAddPropertyModalOpen(false)}
        ariaLabel="Add New Property"
      >
        <div className={styles.searchModalContent}>
          <div className={styles.modalHeader}>
            <div className={styles.modalIconWrap}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <div className={styles.modalHeaderText}>
              <h3 className={styles.modalTitle}>Search & Add Property</h3>
              <p className={styles.modalSubtitle}>
                Andy will automatically look up your assessed value and tax details
              </p>
            </div>
          </div>

          <AddressSearch
            placeholder="Enter Texas address (e.g. street, city, or zip)"
            buttonText="Add Property"
            preventInternalSubmit={true}
            isSubmitting={isSubmitting}
            searchAddresses={searchAddresses}
            className={styles.modalAddressSearch}
            onAddressSelect={() => {
              // Intentionally blank: wait for explicit button click
            }}
            onButtonClick={(addressStr, suggestion) => {
              if (suggestion) {
                handleAddPropertySubmit({
                  address: suggestion.address,
                  city: suggestion.city,
                  state: suggestion.state,
                  zipCode: suggestion.zipCode,
                  apn: suggestion.apn,
                });
              } else if (addressStr.trim()) {
                handleAddPropertySubmit({
                  address: addressStr,
                });
              }
            }}
          />
        </div>
      </Modal>

      {/* ================================================================
          Service Expansion Request Modal
          ================================================================ */}
      <Modal
        isOpen={isServiceRequestModalOpen}
        onClose={handleCloseServiceRequest}
        ariaLabel="Service Expansion Request"
        hideBackdrop={false}
      >
        <div className={styles.serviceRequestModalContent}>
          <div className={styles.srHeader}>
            <img src={Images.propertyHomeMarker} alt="Map Pin" className={styles.srHeaderIcon} />
            <h3 className={styles.srTitle}>Currently Available in Texas Only</h3>
            <p className={styles.srDescription}>
              We&apos;re currently offering our services exclusively in the state of Texas. Our team
              is actively working to expand to additional states very soon. If you&apos;d like to be
              notified when we launch in your area, please share your details below.
            </p>
          </div>

          <div className={styles.srFormContainer}>
            <h4 className={styles.srFormTitle}>Stay Updated When We Launch in Your State</h4>

            <form onSubmit={handleSubmitServiceRequest} className={styles.srForm}>
              <div className={styles.srFormGroup}>
                <label className={styles.srLabel}>Full Name</label>
                <div className={styles.srInputWrapper}>
                  <svg
                    className={styles.srInputIcon}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <input
                    type="text"
                    className={styles.srInput}
                    placeholder="Enter your full name"
                    value={serviceRequestForm.name}
                    onChange={(e) =>
                      setServiceRequestForm({ ...serviceRequestForm, name: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className={styles.srFormGroup}>
                <label className={styles.srLabel}>Phone Number</label>
                <div className={styles.srInputWrapper}>
                  <svg
                    className={styles.srInputIcon}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  <input
                    type="tel"
                    className={styles.srInput}
                    placeholder="Enter your phone number"
                    value={serviceRequestForm.phone}
                    onChange={(e) =>
                      setServiceRequestForm({ ...serviceRequestForm, phone: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className={styles.srFormGroup}>
                <label className={styles.srLabel}>Email Address</label>
                <div className={styles.srInputWrapper}>
                  <svg
                    className={styles.srInputIcon}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  <input
                    type="email"
                    className={styles.srInput}
                    placeholder="Enter your email address"
                    value={serviceRequestForm.email}
                    onChange={(e) =>
                      setServiceRequestForm({ ...serviceRequestForm, email: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className={styles.srFormRow}>
                <div className={styles.srFormGroup}>
                  <label className={styles.srLabel}>State</label>
                  <div className={styles.srInputWrapper}>
                    <svg
                      className={styles.srInputIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <input
                      type="text"
                      className={styles.srInput}
                      placeholder="Enter your state"
                      value={serviceRequestForm.state}
                      onChange={(e) =>
                        setServiceRequestForm({ ...serviceRequestForm, state: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>

                <div className={styles.srFormGroup}>
                  <label className={styles.srLabel}>City</label>
                  <div className={styles.srInputWrapper}>
                    <svg
                      className={styles.srInputIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
                      <line x1="12" y1="18" x2="12" y2="18.01" />
                      <line x1="8" y1="18" x2="8" y2="18.01" />
                      <line x1="16" y1="18" x2="16" y2="18.01" />
                      <line x1="12" y1="14" x2="12" y2="14.01" />
                      <line x1="8" y1="14" x2="8" y2="14.01" />
                      <line x1="16" y1="14" x2="16" y2="14.01" />
                      <line x1="12" y1="10" x2="12" y2="10.01" />
                      <line x1="8" y1="10" x2="8" y2="10.01" />
                      <line x1="16" y1="10" x2="16" y2="10.01" />
                    </svg>
                    <input
                      type="text"
                      className={styles.srInput}
                      placeholder="Enter your city"
                      value={serviceRequestForm.city}
                      onChange={(e) =>
                        setServiceRequestForm({ ...serviceRequestForm, city: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>
              </div>

              <div className={styles.srCheckboxGroup}>
                <input
                  type="checkbox"
                  id="srConsent"
                  className={styles.srCheckbox}
                  checked={serviceRequestForm.consent}
                  onChange={(e) =>
                    setServiceRequestForm({ ...serviceRequestForm, consent: e.target.checked })
                  }
                />
                <label htmlFor="srConsent" className={styles.srCheckboxLabel}>
                  I agree to be contacted when services become available in my area.
                </label>
              </div>

              <div className={styles.srActions}>
                <button
                  type="submit"
                  className={styles.srSubmitBtn}
                  disabled={isSubmittingServiceRequest || !serviceRequestForm.consent}
                >
                  {isSubmittingServiceRequest ? "Submitting..." : "Notify Me When Available"}
                </button>
                <button
                  type="button"
                  className={styles.srCancelBtn}
                  onClick={handleCloseServiceRequest}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  Back to Home
                </button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </div>
  );
};
